<template>
  <div class="sign-agreement-wrapper">
    <div class="route-name">{{ $t(`message.sideMenu.${this.$route.name}`) }}</div>
    <div class="sign-agreement-container">
      <iframe
        v-if="documentViewerUrl"
        :src="documentViewerUrl"
        class="agreement-document"
        title="personal-data-agreement-document"
      />
      <div class="sign-block">
        <div class="sign-title">{{ $t('message.personalDataAgreementPage.signTitle') }}</div>
        <div class="sign-offer-item" @click.stop="checkboxHandler()">
          <div
            class="check-box-btn"
            :class="{ 'check-box-checked': isAgreementAgreed }"
          >
            <img
              v-if="isAgreementAgreed"
              class="check-box-image"
              src="../../../../public/custom/images/check.svg"
              alt="Checked image"
            />
          </div>
          <div class="check-box-label">
            {{ $t('message.personalDataAgreementPage.checkboxLabel') }}
          </div>
        </div>
        <div class="otp-container">
          <div class="otp-block">
            <base-text-input
              :mask-pattern="'######'"
              :name="'personalDataOtp'"
              :validate-rules="'otp'"
              :placeholder="$t('message.contract.otp.placeholder')"
              v-model="otp"
              :disabled="!isAgreementAgreed"
              :is-has-error-block="false"
            />
          </div>
          <div class="send-otp-btn">
            <base-button
              :text="$t('message.personalDataAgreementPage.getOtpBtn')"
              :disabled="!isAgreementAgreed"
              @click="getOtpCode()"
              min-height="40px"
            />
          </div>
        </div>
        <div class="signer-block">
          <div class="signer-title">{{ $t('message.personalDataAgreementPage.signerTitle') }}</div>
          <div class="signer-full-name">{{ signerFullName }}</div>
        </div>
        <div class="sign-agreement-btn">
          <base-button
            :text="$t('message.personalDataAgreementPage.signBtn')"
            :disabled="isSignBtnDisabled"
            :is-loading="isSignBtnLoading"
            @click="signAgreement()"
            min-height="40px"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BaseTextInput from '../../../components/base/BaseTextInput.vue';
import BaseButton from '../../../components/base/BaseButton.vue';
import { mapGetters } from 'vuex';

export default {
  name: 'SignPersonalDataAgreement',
  inject: ['$httpService'],
  components: {
    BaseTextInput,
    BaseButton
  },
  data() {
    return {
      agreementDocumentId: '',
      blobUrl: '',
      isAgreementAgreed: false,
      isOtpRequested: false,
      isSignBtnLoading: false,
      otp: ''
    };
  },
  computed: {
    ...mapGetters(['profile']),
    documentViewerUrl() {
      if (!this.blobUrl) {
        return '';
      }

      return `${this.blobUrl}#toolbar=0&navpanes=0&scrollbar=0&statusbar=0&zoom=100`;
    },
    signerFullName() {
      const lastName = this.profile?.name_ru?.family_name_ru || '';
      const firstName = this.profile?.name_ru?.given_name_ru || '';
      const middleName = this.profile?.name_ru?.middle_name_ru || '';

      return [lastName, firstName, middleName].filter(Boolean).join(' ');
    },
    isSignBtnDisabled() {
      return !this.isAgreementAgreed
        || !this.otp
        || this.errors.has('personalDataOtp')
        || !this.agreementDocumentId
        || this.isSignBtnLoading;
    }
  },
  async created() {
    this.$store.dispatch('loading', true);

    try {
      const createResponse = await this.$httpService.agreement.createPersonalDataAgreement();
      const agreementDocumentId = createResponse?.data?.document_id;

      if (!agreementDocumentId) {
        throw new Error('Agreement document_id is missing');
      }

      this.agreementDocumentId = agreementDocumentId;

      const downloadResponse = await this.$httpService.agreement.downloadPersonalDataAgreement(agreementDocumentId);
      this.showDocument(downloadResponse);
    } catch (error) {
      this.$toast.error(this.$t('message.personalDataAgreementPage.createError'));
      this.$router.push({ name: 'change-personal-data-agreement' });
    } finally {
      this.$store.dispatch('loading', false);
    }
  },
  beforeDestroy() {
    this.revokeDocumentUrl();
  },
  methods: {
    checkboxHandler() {
      this.isAgreementAgreed = !this.isAgreementAgreed;

      if (!this.isAgreementAgreed) {
        this.otp = '';
        this.isOtpRequested = false;
      }
    },
    async getOtpCode() {
      if (!this.isAgreementAgreed) {
        return;
      }

      try {
        await this.$httpService.loan.getOtpCode();
        this.isOtpRequested = true;
        this.$toast.success(this.$t('message.signIn.otpToastMsg'));
      } catch (error) {
        this.$toast.error(this.$t('message.personalDataAgreementPage.otpError'));
      }
    },
    async signAgreement() {
      if (this.isSignBtnDisabled) {
        return;
      }

      const agreement_id = this.agreementDocumentId;

      if (!agreement_id) {
        this.$toast.error(this.$t('message.personalDataAgreementPage.signError'));
        return;
      }

      this.isSignBtnLoading = true;

      try {
        const otp = +this.otp;
        const response = await this.$httpService.agreement.signPersonalDataAgreement(agreement_id, otp);

        if (!response) {
          throw new Error('Personal data agreement sign failed');
        }

        this.$toast.success(this.$t('message.personalDataAgreementPage.signSuccess'));
        this.$router.push({ name: 'change-personal-data-agreement' });
      } catch (error) {
        this.$toast.error(this.$t('message.personalDataAgreementPage.signError'));
      } finally {
        this.isSignBtnLoading = false;
      }
    },
    showDocument(response) {
      const contentType = response.headers['content-type'] || 'application/pdf';
      const blob = new Blob([response.data], { type: contentType });

      this.revokeDocumentUrl();
      this.blobUrl = window.URL.createObjectURL(blob);
    },
    revokeDocumentUrl() {
      if (this.blobUrl) {
        window.URL.revokeObjectURL(this.blobUrl);
        this.blobUrl = '';
      }
    }
  }
};
</script>

<style lang="scss" scoped>
  .sign-agreement-wrapper {
    @include page-wrapper-with-header-and-footer;

    padding-top: 40px;
    box-sizing: border-box;
  }

  .sign-agreement-container {
    @include base-page-container;

    flex-direction: column;
    min-height: calc(100vh - 434px);
  }

  .route-name {
    @include route-name;
    width: 95%;
    text-align: start;
    margin-bottom: 20px;
    margin-top: -20px;
  }

  .agreement-document {
    width: 100%;
    flex: 1;
    min-height: 700px;
    border: none;
    border-radius: 10px;
    background-color: #ffffff;
  }

  .sign-block {
    width: 100%;
    margin-top: 40px;
    display: flex;
    flex-direction: column;
    margin-bottom: 60px;
  }

  .sign-title {
    margin-bottom: 20px;
    font-weight: 700;
    font-size: 16px;
    line-height: 135%;
    color: #121212;
  }

  .sign-offer-item {
    margin-bottom: 30px;
    display: flex;
    align-items: flex-start;
    cursor: pointer;
  }

  .check-box-btn {
    @include base-check-box;
    width: 22px;
    height: 22px;
    flex-shrink: 0;
  }

  .check-box-image {
    width: 12px;
  }

  .check-box-label {
    @include base-check-box-label;

    font-size: 16px;
    line-height: 135%;
    font-weight: 500;
  }

  .otp-container {
    display: flex;
    user-select: none;
  }

  .otp-block {
    width: 280px;
    margin-right: 20px;
  }

  .send-otp-btn {
    width: 160px;
    height: 40px;
  }

  .signer-block {
    width: 100%;
    margin-top: 30px;
    display: flex;
    flex-direction: column;
  }

  .signer-title {
    margin-bottom: 10px;
    font-weight: 600;
    font-size: 16px;
    line-height: 135%;
    color: #121212;
  }

  .signer-full-name {
    font-weight: 400;
    font-size: 16px;
    line-height: 135%;
    color: #121212;
    text-transform: uppercase;
  }

  .sign-agreement-btn {
    width: 260px;
    min-width: 260px;
    max-width: 260px;
    height: 40px;
    margin-top: 30px;
    flex-shrink: 0;
  }

  @media screen and (max-width: 620px) {
    .otp-container {
      flex-direction: column-reverse;
      gap: 12px;
    }

    .otp-block {
      width: 100%;
      margin-right: 0;
    }

    .send-otp-btn {
      width: 100%;
    }

    .sign-agreement-btn {
      width: 100%;
      min-width: 100%;
      max-width: 100%;
    }
  }
</style>
